import { createTheme } from '@mui/material';
import cssVars from '@figma/fpl-tokens/css-vars';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { color, EL_COLORS } from '@/colors';

const LEGACY_FONT_FAMILY =
  '"DM Sans", system-ui, -apple-system, Arial, "Apple Color Emoji", "Segoe UI Emoji", sans-serif';
const FPL_FONT_FAMILY = '"Inter", system-ui, -apple-system, Arial, "Apple Color Emoji", "Segoe UI Emoji", sans-serif';

type ThemeColorValues = {
  white100: string;
  white64: string;
  white40: string;
  white16: string;
  white100Secondary: string;
  white64_T: string;
  white16_T: string;
  white08_T: string;
};

const getThemeColorValues = (useFigmaColors: boolean): ThemeColorValues => {
  const { Yellow100, Yellow64, Yellow40, Yellow16, Yellow_Secondary, Yellow64_T, Yellow16_T, Yellow8_T } = color;

  return {
    white100: useFigmaColors ? color.White100 : Yellow100,
    white64: useFigmaColors ? color.White64 : Yellow64,
    white40: useFigmaColors ? color.White40 : Yellow40,
    white16: useFigmaColors ? color.White16 : Yellow16,
    white100Secondary: useFigmaColors ? color.White100 : Yellow_Secondary,
    white64_T: useFigmaColors ? color.White64_T : Yellow64_T,
    white16_T: useFigmaColors ? color.White16_T : Yellow16_T,
    white08_T: useFigmaColors ? color.White08_T : Yellow8_T,
  };
};

type DarkThemeOptions = {
  useFigmaColors: boolean;
  useFplTypography: boolean;
};

export const createDarkTheme = ({ useFigmaColors, useFplTypography }: DarkThemeOptions) => {
  const themeColors = getThemeColorValues(useFigmaColors);
  const appFontFamily = useFplTypography ? FPL_FONT_FAMILY : LEGACY_FONT_FAMILY;
  const themeCssVars: Record<`--weavy-theme-${string}`, string> = {
    '--weavy-theme-white100': themeColors.white100,
    '--weavy-theme-white64': themeColors.white64,
    '--weavy-theme-white40': themeColors.white40,
    '--weavy-theme-white16': themeColors.white16,
    '--weavy-theme-white100-secondary': themeColors.white100Secondary,
    '--weavy-theme-white64-transparent': themeColors.white64_T,
    '--weavy-theme-white16-transparent': themeColors.white16_T,
    '--weavy-theme-white8-transparent': themeColors.white08_T,
    '--weavy-theme-timeline-selected-outline-color': useFigmaColors ? color.White100 : themeColors.white64,
    '--weavy-theme-timeline-selected-outline-width': useFigmaColors ? '2px' : '1.5px',
  };

  return createTheme({
    components: {
      MuiButton: {
        defaultProps: {
          disableElevation: true,
        },
        styleOverrides: {
          root: {
            textTransform: 'none',
          },
          sizeSmall: {
            fontSize: '0.75rem',
            fontWeight: 500,
            lineHeight: 1.5,
          },
          sizeMedium: {
            fontSize: '0.875rem',
            fontWeight: 500,
            lineHeight: 1.5,
          },
          sizeLarge: {
            fontSize: '0.875rem',
            fontWeight: 500,
            lineHeight: 1.5,
          },
        },
      },
      MuiButtonBase: {
        styleOverrides: {
          root: {
            fontFamily: appFontFamily,
          },
        },
      },
      MuiCircularProgress: {
        defaultProps: {
          disableShrink: true,
        },
      },
      MuiCssBaseline: {
        styleOverrides: {
          ':root': themeCssVars,
          html: {
            color: token.text,
            background: color.Black100,
            fontFamily: appFontFamily,
          },
          body: {
            color: token.text,
            fontFamily: appFontFamily,
          },
          ...(useFplTypography ? { '#weavy-main': { backgroundColor: cssVars.color.bg } } : {}),
          ...(useFplTypography
            ? {
                'body .media-container-dark': {
                  backgroundColor: cssVars.color.bg,
                  backgroundImage: `linear-gradient(45deg, ${cssVars.color.bgSecondary} 25%, transparent 25%), linear-gradient(-45deg, ${cssVars.color.bgSecondary} 25%, transparent 25%), linear-gradient(45deg, transparent 75%, ${cssVars.color.bgSecondary} 75%), linear-gradient(-45deg, transparent 75%, ${cssVars.color.bgSecondary} 75%)`,
                  backgroundSize: '30px 30px',
                  backgroundPosition: '0 0, 0 15px, 15px -15px, -15px 0px',
                },
              }
            : {}),
          '.MuiButtonBase-root, .MuiButton-root': {
            color: token.text,
          },
          '.MuiTypography-root': {
            color: token.text,
            ...(useFplTypography ? {} : { fontFamily: appFontFamily }),
          },
          '.MuiTabs-indicator': {
            backgroundColor: `${color.Yambo_Purple} !important`,
          },
          // MUI's CssBaseline resets `b, strong { font-weight: 700 }` as an *unlayered* global.
          // FPL components (e.g. Badge) render as `<strong>` and set their own weight (450) inside
          // an `@layer`, and unlayered rules always beat layered ones regardless of specificity — so
          // the reset wins and FPL badges render bold. `revert-layer` clears MUI's forced value for
          // these elements: FPL's layered weight (450) now applies to FPL components, while plain
          // `<b>`/`<strong>` fall back to the user-agent bold default. Only applied on the FPL path.
          ...(useFplTypography
            ? {
                'b, strong': {
                  fontWeight: 'revert-layer',
                },
              }
            : {}),
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            fontFamily: useFplTypography ? appFontFamily : '"DM Sans", sans-serif',
            fontSize: '0.75rem',
            fontWeight: 500,
            lineHeight: 1.5,
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontFamily: useFplTypography ? appFontFamily : '"DM Mono"',
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: token.text,
            textDecorationColor: token.text,
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          root: {
            borderRadius: 4,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '&:hover': {
              borderRadius: '4px',
              backgroundColor: token.bg,
            },
          },
        },
      },
      MuiListItemText: {
        styleOverrides: {
          primary: {
            fontFamily: useFplTypography ? appFontFamily : '"DM Sans", sans-serif',
            fontWeight: 400,
            fontSize: '0.875rem',
          },
        },
      },
      MuiMenu: {
        defaultProps: {
          slotProps: {
            paper: {
              sx: {
                backgroundColor: color.Black100,
              },
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: '0.75rem',
            color: token.text,
            '&.Mui-selected': {
              backgroundColor: token.bg,
              '&:hover': {
                backgroundColor: token.bgSelected,
              },
              '&:focus': {
                backgroundColor: token.bg,
              },
            },
          },
        },
      },
      MuiRadio: {
        defaultProps: {
          disableRipple: true,
          disableFocusRipple: true,
        },
        styleOverrides: {
          root: {
            color: token.text,
            '&.Mui-checked': {
              color: token.text,
            },
            '&:hover': {
              backgroundColor: token.bgSecondary,
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            fontFamily: useFplTypography ? appFontFamily : '"DM Sans", sans-serif',
            fontSize: '0.75rem',
            fontWeight: 500,
            lineHeight: 1.5,
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          /**
           * A table's scroll container has to be a containing block, not just a scroller.
           *
           * Rows routinely hold screen-reader-only content — an FPL toggle, for instance, renders a
           * hidden `<label>` with `position: absolute` and no offsets. Against a `position: static`
           * container that label resolves against the initial containing block, so it is not clipped
           * by the scroller: the browser grows the *document* to reach the label's static position
           * instead. The window then scrolls the whole app shell and exposes the black `#root`
           * background beneath it.
           */
          root: {
            position: 'relative',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            backgroundColor: useFplTypography ? cssVars.color.bg : color.Black100,
            borderBottom: `1px solid ${token.border}`,
          },
          head: {
            fontFamily: useFplTypography ? appFontFamily : '"DM Mono", monospace',
            fontWeight: 400,
            color: token.text,
            fontSize: '0.75rem',
          },
          body: {
            fontFamily: useFplTypography ? appFontFamily : '"DM Sans", sans-serif',
            fontWeight: 500,
            fontSize: '0.75rem',
            padding: '16px 4px',
            backgroundColor: 'transparent',
          },
        },
      },
      MuiTypography: {
        defaultProps: {
          variant: 'body-std-rg',
          variantMapping: {
            h1: 'span',
            h2: 'span',
            h3: 'span',
            'body-xxl-rg': 'span',
            'body-xl-rg': 'span',
            'body-lg-md': 'span',
            'body-lg-rg': 'span',
            'body-lg-sb': 'span',
            'body-sm-md': 'span',
            'body-sm-rg': 'span',
            'body-std-md': 'span',
            'body-std-rg': 'span',
            'body-xs-rg': 'span',
            'label-sm-rg': 'span',
            'label-xs-rg': 'span',
            unstyled: 'span',
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: token.bgSelected,
            color: token.text,
            borderRadius: '4px',
            border: `1px solid ${EL_COLORS.BoxBorder}`,
            fontSize: '0.75rem',
            fontWeight: 400,
            padding: '2px 4px',
          },
        },
      },
    },
    palette: {
      mode: 'dark',
      weavy_cta: {
        main: color.Yambo_Purple,
        light: color.Yambo_Green_Stroke,
        dark: color.Yambo_Purple_Stroke,
        contrastText: color.White100,
      },
      weavy_cta_secondary: {
        main: color.Yambo_CTA_BG,
        light: color.White100,
        dark: color.Yambo_White_BG,
        contrastText: color.Yambo_Text_On_White,
      },
      weavy_cta_blue: {
        main: color.Yambo_Blue,
        light: color.Yambo_Blue_Stroke,
        dark: color.Yambo_Blue_Stroke,
        contrastText: color.White100,
      },
      weavy_green_outline: {
        main: color.Yambo_Green_Stroke,
        light: color.Yambo_Green_Stroke,
        dark: color.Yambo_Green_Stroke,
        contrastText: color.White100,
      },
      weavy_yellow_cta: {
        main: themeColors.white100,
        light: themeColors.white64,
        dark: themeColors.white100Secondary,
        contrastText: color.Black100,
      },
      success: {
        main: color.Yambo_Purple,
      },
      weavy_black: {
        main: color.Black100,
        light: color.Black92,
        contrastText: color.White100,
      },
    },
    typography: {
      fontFamily: appFontFamily,
      h1: { fontSize: '1.75rem', fontWeight: 700 },
      h2: { fontSize: '1.5rem', fontWeight: 500 },
      h3: { fontSize: '1.125rem', fontWeight: 500 },
      'body-xxl-rg': { fontSize: '1.5rem', fontWeight: 400 },
      'body-xl-rg': { fontSize: '1.25rem', fontWeight: 400 },
      'body-lg-sb': { fontSize: '1rem', fontWeight: 600 },
      'body-lg-md': { fontSize: '1rem', fontWeight: 500 },
      'body-lg-rg': { fontSize: '1rem', fontWeight: 400 },
      'body-std-md': { fontSize: '0.875rem', fontWeight: 500 },
      'body-std-rg': { fontSize: '0.875rem', fontWeight: 400 },
      'body-sm-md': { fontSize: '0.75rem', fontWeight: 500 },
      'body-sm-rg': { fontSize: '0.75rem', fontWeight: 400 },
      'body-xs-rg': { fontSize: '0.625rem', fontWeight: 400 },
      'label-sm-rg': { fontFamily: '"DM Mono"', fontSize: '0.75rem', fontWeight: 400 },
      'label-xs-rg': { fontFamily: '"DM Mono"', fontSize: '0.625rem', fontWeight: 400 },
      // Style-less carrier variant: AppTypography renders MUI Typography with this so
      // there are no MUI typography styles to compete with the StyleX/FPL classes it applies.
      unstyled: undefined,
      caption: undefined,
      body1: undefined,
      body2: undefined,
      h4: undefined,
      h5: undefined,
      h6: undefined,
      subtitle1: undefined,
      subtitle2: undefined,
      button: undefined,
      overline: undefined,
    },
  });
};

export const darkTheme = createDarkTheme({ useFigmaColors: false, useFplTypography: false });
